import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Briefcase,
    ClipboardList,
    Compass,
    Download,
    Microscope,
    Palette,
    Users,
    Wrench
} from 'lucide-react';
import { buildFullCareerReport } from './fullReportData';
import FullResultsPrintPage from './FullResultsPrintPage';

const ORIENTATION_ICONS = {
    R: Wrench,
    I: Microscope,
    A: Palette,
    S: Users,
    E: Briefcase,
    C: ClipboardList
};

const RAPID_MODEL_IMAGE_SRC = '/rapid.jpeg';

const choiceToneClasses = {
    'Self-Driven': 'border-[#e9dcfb] bg-[#faf5ff] text-[#7c3aed]',
    'Seek out New': 'border-[#dbeafe] bg-[#eff6ff] text-[#2563eb]',
    'Others-Driven': 'border-[#e5e7eb] bg-[#f8fafc] text-[#475569]',
    Calculated: 'border-[#dcfce7] bg-[#f0fdf4] text-[#15803d]',
    Rational: 'border-[#dbeafe] bg-[#eff6ff] text-[#1d4ed8]',
    Emotional: 'border-[#ffedd5] bg-[#fff7ed] text-[#ea580c]',
    Positive: 'border-[#dcfce7] bg-[#f0fdf4] text-[#15803d]',
    Neutral: 'border-[#fef3c7] bg-[#fffbeb] text-[#b45309]',
    Negative: 'border-[#fee2e2] bg-[#fef2f2] text-[#dc2626]',
    'Optimal Usage': 'border-[#dcfce7] bg-[#f0fdf4] text-[#15803d]',
    'Under Usage': 'border-[#fef3c7] bg-[#fffbeb] text-[#b45309]',
    'Over Usage': 'border-[#fee2e2] bg-[#fef2f2] text-[#dc2626]'
};

const SectionHeading = ({ eyebrow, title, intro }) => (
    <div className="mb-8">
        <span className="inline-flex items-center rounded-full border border-[#eadff0] bg-white/80 px-4 py-1.5 text-xs font-black uppercase tracking-[0.22em] text-[#92278F]">
            {eyebrow}
        </span>
        <h2 className="mt-[30px] text-3xl md:text-5xl font-bold text-[#171717] brand-font leading-tight">
            {title}
        </h2>
        <p className="mt-4 max-w-3xl text-base md:text-lg leading-relaxed text-[var(--color-subtext)] brand-text">
            {intro}
        </p>
    </div>
);

const FullResultGate = ({ formData, isSubmitting, onChange, onSubmit }) => {
    return (
        <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 w-full animate-fadeIn pb-20 pt-5">
            <div className="w-full max-w-md bg-white shadow-xl rounded-3xl p-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 h-3 w-full bg-gradient-to-r from-[#F37741] to-[#92278F]"></div>
                <h2 className="mt-4 text-2xl font-bold text-center text-[#2d3748] brand-font">Unlock your full career report</h2>
                <p className="mt-3 mb-8 text-center text-gray-500 brand-text leading-relaxed">
                    Please enter your details to view the updated premium report with distinct Part A and Part B insights.
                </p>

                <form onSubmit={onSubmit} className="flex flex-col gap-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Name</label>
                        <input
                            required
                            name="name"
                            value={formData.name}
                            onChange={onChange}
                            className="w-full rounded-xl border border-gray-300 p-3 outline-none transition-all focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F]"
                            placeholder="Your Name"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 border-r border-gray-300 pr-2 font-bold text-gray-500">+91</span>
                            <input
                                required
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={onChange}
                                className="w-full rounded-xl border border-gray-300 p-3 pl-14 outline-none transition-all focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F]"
                                placeholder="Your Phone Number"
                                maxLength={10}
                            />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="w-1/2">
                            <label className="block text-sm font-bold text-gray-700 mb-1">Age</label>
                            <input
                                required
                                type="number"
                                name="age"
                                value={formData.age}
                                onChange={onChange}
                                className="w-full rounded-xl border border-gray-300 p-3 outline-none transition-all focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F]"
                                placeholder="Age"
                            />
                        </div>
                        <div className="w-1/2">
                            <label className="block text-sm font-bold text-gray-700 mb-1">Gender</label>
                            <select
                                required
                                name="gender"
                                value={formData.gender}
                                onChange={onChange}
                                className="w-full rounded-xl border border-gray-300 bg-white p-3 outline-none transition-all focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F]"
                            >
                                <option value="">Select</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`mt-6 w-full rounded-xl py-3 text-lg font-bold text-white shadow-md transition-transform active:scale-95 ${isSubmitting ? 'bg-gray-400 cursor-wait' : 'bg-gradient-to-r from-[#F37741] to-[#92278F] hover:shadow-lg'}`}
                    >
                        {isSubmitting ? 'Generating Report...' : 'View Full Report'}
                    </button>
                </form>
            </div>
        </div>
    );
};

const PartAIntroPanel = ({ intro }) => (
    <div className="rounded-[1.5rem] border border-[#eadff0] bg-white/95 p-4 shadow-sm full-report-card sm:rounded-[1.85rem] sm:p-6">
        <div className="space-y-4 text-base leading-relaxed text-[var(--color-subtext)] brand-text">
            {intro.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
            ))}
        </div>
    </div>
);

const PartACareerPreferencesPanel = ({ intro }) => (
    <div className="rounded-[1.5rem] border border-[#f2dccf] bg-[linear-gradient(180deg,_#fff8f3_0%,_#ffffff_100%)] p-4 shadow-sm full-report-card sm:rounded-[1.85rem] sm:p-6">
        <p className="text-sm font-black uppercase tracking-[0.22em] text-[#F37741]">{intro.careerPreferencesTitle}</p>

        <div className="mt-5 space-y-4 text-base leading-relaxed text-[var(--color-subtext)] brand-text">
            {intro.careerPreferences.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
            ))}
        </div>

    </div>
);

const PartATraitExplanationCard = ({ trait, index }) => {
    const Icon = ORIENTATION_ICONS[trait.code] || Compass;

    return (
        <article className="rounded-[1.5rem] border border-[#efe6dd] bg-white p-4 shadow-sm full-report-card sm:rounded-[2rem] sm:p-6 md:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#f1e8de] pb-5">
                <div className="flex items-start gap-4">
                    <div
                        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border bg-white"
                        style={{ borderColor: `${trait.color}50` }}
                    >
                        <Icon size={26} color={trait.color} strokeWidth={2.3} />
                    </div>

                    <div>
                        <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#8a7d74]">Top {index + 1} trait · {trait.code}</p>
                        <h3 className="mt-2 text-2xl md:text-3xl font-black leading-tight text-[#171717] brand-font">{trait.heading}</h3>
                        <p className="mt-2 text-sm font-bold brand-text" style={{ color: trait.color }}>{trait.percent}% fit signal</p>
                    </div>
                </div>

                <span className="rounded-full bg-[#f8f3fb] px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-[#92278F]">
                    Rank #{trait.rank}
                </span>
            </div>

            <div className="mt-6 space-y-4 text-base leading-relaxed text-[var(--color-subtext)] brand-text">
                {trait.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                ))}
            </div>
        </article>
    );
};

const PartATraitCareerPanel = ({ trait }) => (
    <article className="rounded-[1.5rem] border border-[#efe6dd] bg-white p-4 shadow-sm full-report-card sm:rounded-[2rem] sm:p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#92278F]">Possible careers</p>
                <h3 className="mt-2 text-2xl md:text-3xl font-black leading-tight text-[#171717] brand-font">{trait.careerHeading}</h3>
                <p className="mt-3 text-base leading-relaxed text-[var(--color-subtext)] brand-text">{trait.careerIntro}</p>
            </div>

            <span className="rounded-full border px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em]" style={{ borderColor: `${trait.color}35`, color: trait.color, backgroundColor: `${trait.color}12` }}>
                {trait.title}
            </span>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {trait.careers.map((career) => (
                <div key={`${trait.code}-${career}`} className="rounded-2xl border border-[#efe6dd] bg-[#fffdfb] px-4 py-3 text-sm font-bold text-[#2d2a27] brand-text">
                    {career}
                </div>
            ))}
        </div>

        <div className="mt-7 rounded-[1.5rem] border border-[#eadff0] bg-[linear-gradient(135deg,_rgba(146,39,143,0.08),_rgba(243,119,65,0.08))] px-5 py-4 text-center">
            <p className="text-sm md:text-base font-black text-[#171717] brand-font">{trait.counsellorMessage}</p>
        </div>
    </article>
);

const ReferenceTextSection = ({ title, paragraphs, note, eyebrow, chip, showGuidance = false }) => (
    <section className="rounded-[1.5rem] border border-[#efe6dd] bg-white/95 p-4 shadow-sm full-report-card sm:rounded-[2rem] sm:p-6 md:p-8">
        {eyebrow ? <p className="text-sm font-black uppercase tracking-[0.24em] text-[#92278F]">{eyebrow}</p> : null}
        {title ? (
            <div className="flex flex-wrap items-center gap-3">
                <h3 className="text-xl font-black leading-tight text-[#171717] brand-font sm:text-2xl md:text-3xl">{title}</h3>
                {chip ? (
                    <span className="rounded-full border border-[#eadff0] bg-[#f8f3fb] px-4 py-1.5 text-xs font-black uppercase tracking-[0.16em] text-[#92278F]">
                        {chip}
                    </span>
                ) : null}
            </div>
        ) : null}
        <div className={`${title || eyebrow ? 'mt-5' : ''} space-y-4 text-sm leading-relaxed text-[#2d2a27] brand-text sm:text-base`}>
            {paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
            ))}
        </div>
        {note ? <p className="mt-8 text-sm leading-relaxed text-[#2d2a27] brand-text sm:text-base">{note}</p> : null}
        {showGuidance ? <GuidanceSupportCard /> : null}
    </section>
);

const GuidanceSupportCard = () => (
    <div className="rounded-[1.5rem] border border-[#eadff0] bg-[linear-gradient(135deg,_rgba(146,39,143,0.08),_rgba(243,119,65,0.08))] px-5 py-4 text-center shadow-sm full-report-card">
        <p className="text-sm md:text-base font-black text-[#171717] brand-font">Contact your Yancha Career Councellor for InDepth Analysis</p>
    </div>
);

const RapidModelDiagram = () => (
    <div className="my-8 flex justify-center px-2">
        <img
            src={RAPID_MODEL_IMAGE_SRC}
            alt="RAPiD model showing Attitude, Perspectives, Decision Making and Resources around choices I make"
            className="h-auto w-full max-w-[430px] rounded-2xl object-contain"
        />
    </div>
);

const ResourceScale = () => (
    <div className="mx-auto w-full max-w-4xl overflow-x-auto">
        <div className="grid min-w-[520px] grid-cols-4 overflow-hidden rounded-t-lg text-center text-xs font-black text-white brand-text sm:text-sm">
            <div className="bg-[#4f81bd] px-3 py-2">Resources</div>
            <div className="bg-[#00b050] px-3 py-2">Optimal Usage</div>
            <div className="bg-[#ffc000] px-3 py-2">Under Usage</div>
            <div className="bg-[#ff0000] px-3 py-2">Over usage</div>
        </div>
    </div>
);

const AttitudeScale = () => (
    <div className="mx-auto w-full max-w-4xl overflow-x-auto">
        <div className="grid min-w-[520px] grid-cols-4 overflow-hidden rounded-t-lg text-center text-xs font-black text-white brand-text sm:text-sm">
            <div className="bg-[#4f81bd] px-3 py-2">Attitude</div>
            <div className="bg-[#00b050] px-3 py-2">Positive</div>
            <div className="bg-[#ffc000] px-3 py-2">Neutral</div>
            <div className="bg-[#ff0000] px-3 py-2">Negative</div>
        </div>
    </div>
);

const CareerChoiceMatrix = ({ matrix }) => {
    const columns = ['Calculated', 'Rational', 'Emotional'];

    return (
        <div className="overflow-x-auto rounded-2xl border border-[#efe6dd] bg-white shadow-sm">
            <div className="min-w-[720px]">
                <div className="grid grid-cols-[150px_repeat(3,minmax(0,1fr))] bg-white text-center font-black text-[#171717] brand-font">
                    <div className="px-3 py-3 text-left text-sm uppercase tracking-[0.18em] text-[#8a7d74]">Perspectives</div>
                    {columns.map((column) => (
                        <div key={column} className="px-3 py-3 text-lg">{column}</div>
                    ))}
                </div>
                {matrix.map((row, rowIndex) => (
                    <div key={row.perspective} className="grid grid-cols-[150px_repeat(3,minmax(0,1fr))] border-t border-white">
                        <div className="flex items-center bg-white px-3 py-4 text-sm font-black text-[#171717] brand-text">{row.perspective}</div>
                        {columns.map((column) => (
                            <div key={`${row.perspective}-${column}`} className={`${rowIndex === 0 ? 'bg-[#f79646] text-white' : rowIndex === 1 ? 'bg-[#f9d8c8] text-[#171717]' : 'bg-[#faebe6] text-[#171717]'} border-l border-white px-3 py-4 text-sm font-bold leading-relaxed whitespace-pre-line brand-text`}>
                                {row.cells[column]}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

const PartBReferenceSection = ({ report }) => {
    const reference = report.partB.reference;
    const orderedPillars = [
        { ...reference.pillars.perspectives, chip: report.partB.resolvedChoices.perspectives.label },
        { ...reference.pillars.attitude, chip: report.partB.resolvedChoices.attitude.label },
        { ...reference.pillars.resources, chip: report.partB.resolvedChoices.resources.label },
        { ...reference.pillars.decisionMaking, chip: report.partB.resolvedChoices.decision.label }
    ];

    return (
        <section className="rounded-[1.5rem] border border-[#efe6dd] bg-white/70 p-4 shadow-[0_22px_70px_rgba(0,0,0,0.06)] backdrop-blur-xl full-report-card sm:rounded-[2rem] sm:p-6 md:p-10">
            <div className="mb-8">
                <p className="text-sm font-black uppercase tracking-[0.35em] text-[#92278F]">Part B</p>
                <h2 className="mt-6 text-3xl md:text-5xl font-black leading-tight text-[#171717] brand-font">Career choice and RAPiD framework</h2>
            </div>

            <div className="space-y-8">
                <ReferenceTextSection paragraphs={reference.intro} />

                <section className="overflow-hidden rounded-[1.5rem] border border-[#efe6dd] bg-white/95 p-4 shadow-sm full-report-card sm:rounded-[2rem] sm:p-6 md:p-8">
                    <h3 className="text-xl font-black leading-tight text-[#171717] brand-font sm:text-2xl md:text-3xl">{reference.rapidModel.title}</h3>
                    <p className="mt-5 text-sm leading-relaxed text-[#2d2a27] brand-text sm:text-base">{reference.rapidModel.intro}</p>
                    <RapidModelDiagram />
                    <p className="mt-5 text-sm leading-relaxed text-[#2d2a27] brand-text sm:text-base">{reference.rapidModel.note}</p>
                </section>

                <section className="overflow-hidden rounded-[1.5rem] border border-[#efe6dd] bg-white/95 p-4 shadow-sm full-report-card sm:rounded-[2rem] sm:p-6 md:p-8">
                    <h3 className="text-xl font-black leading-tight text-[#171717] brand-font sm:text-2xl md:text-3xl">{reference.careerChoice.title}</h3>
                    <p className="mt-5 text-sm leading-relaxed text-[#2d2a27] brand-text sm:text-base">{reference.careerChoice.intro}</p>
                    <div className="mt-7 space-y-5">
                        <ResourceScale />
                        <CareerChoiceMatrix matrix={report.partB.choiceMatrix} />
                        <AttitudeScale />
                    </div>
                    <p className="mt-6 text-sm leading-relaxed text-[#2d2a27] brand-text sm:text-base">{reference.careerChoice.note}</p>
                </section>

                <ReferenceTextSection title={reference.careerChoices.title} paragraphs={reference.careerChoices.paragraphs} note={reference.careerChoices.note} />

                <div className="grid gap-6 lg:grid-cols-2">
                    {orderedPillars.map((pillar) => (
                        <ReferenceTextSection key={pillar.title} title={pillar.title} paragraphs={pillar.paragraphs} chip={pillar.chip} showGuidance />
                    ))}
                </div>

                <ReferenceTextSection title={reference.recommended.title} paragraphs={reference.recommended.paragraphs} />

                <GuidanceSupportCard />
            </div>
        </section>
    );
};

const ResolvedChoiceCard = ({ choice }) => {
    const toneClasses = choiceToneClasses[choice.label] || 'border-[#eadff0] bg-[#fcf8ff] text-[#92278F]';

    return (
        <div className="rounded-[1.65rem] border border-[#efe6dd] bg-white/92 p-5 shadow-sm full-report-card">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-lg font-black text-[#171717] brand-font">{choice.pillar}</p>
                    <p className="mt-1 text-sm font-semibold text-gray-500 brand-text">{choice.descriptor}</p>
                </div>
                <span className="rounded-full border border-[#efe6dd] bg-[#fffdfb] px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-[#8a7d74]">
                    {choice.emphasis}
                </span>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
                <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${toneClasses}`}>
                    {choice.label}
                </span>
                <span className="text-sm font-bold text-gray-400">{choice.percent}%</span>
            </div>

            <p className="mt-4 text-sm leading-relaxed text-[var(--color-subtext)] brand-text">{choice.summary}</p>
        </div>
    );
};

const FullResultsPage = ({ scores, quizData }) => {
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPreparingPrint, setIsPreparingPrint] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        age: '',
        gender: ''
    });

    const report = useMemo(() => buildFullCareerReport({ scores, quizData }), [scores, quizData]);
    const printDocumentTitle = useMemo(
        () => `${(formData.name || 'Yancha').replace(/\s+/g, '_')}_Career_Report`,
        [formData.name]
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleAfterPrint = () => {
            setIsPreparingPrint(false);
        };

        window.addEventListener('afterprint', handleAfterPrint);

        return () => {
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);

    useEffect(() => {
        if (!isPreparingPrint || typeof document === 'undefined') {
            return undefined;
        }

        const previousTitle = document.title;
        document.title = printDocumentTitle;

        return () => {
            document.title = previousTitle;
        };
    }, [isPreparingPrint, printDocumentTitle]);

    const handlePrint = () => {
        if (isPreparingPrint || typeof window === 'undefined') {
            return;
        }

        setIsPreparingPrint(true);

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                window.print();
            });
        });
    };

    const printStyles = useMemo(() => `
        @page {
            size: A4 portrait;
            margin: 12mm;
        }

        @media screen {
            .full-results-dedicated-print-portal {
                display: none !important;
            }
        }

        @media print {
            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
            }

            html,
            body {
                background: #ffffff !important;
                margin: 0 !important;
                padding: 0 !important;
                width: auto !important;
                min-width: 0 !important;
            }

            body > *:not(.full-results-dedicated-print-portal) {
                display: none !important;
            }

            .full-results-dedicated-print-portal {
                display: block !important;
                visibility: visible !important;
                position: static !important;
                width: 100% !important;
                padding: 0 !important;
                background: #ffffff !important;
            }

            .full-results-dedicated-print-portal,
            .full-results-dedicated-print-portal * {
                visibility: visible !important;
            }

            .full-results-print-toolbar,
            .full-results-live-page {
                display: none !important;
            }

            .full-results-dedicated-print-surface {
                width: 186mm !important;
                margin: 0 auto !important;
            }

            .full-results-dedicated-print-page {
                break-after: page !important;
                page-break-after: always !important;
                overflow: visible !important;
                box-shadow: none !important;
            }

            .full-results-dedicated-print-page:last-child {
                break-after: auto !important;
                page-break-after: auto !important;
            }

            .full-results-dedicated-print-section,
            .full-results-dedicated-print-grid,
            .full-results-dedicated-print-card {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
            }
        }
    `, []);

    const handleChange = (event) => {
        const { name, value } = event.target;

        if (name === 'phone') {
            const numbersOnly = value.replace(/[^0-9]/g, '').slice(0, 10);
            setFormData((previous) => ({ ...previous, [name]: numbersOnly }));
            return;
        }

        setFormData((previous) => ({ ...previous, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (formData.phone.length !== 10) {
            alert('Please enter a valid 10-digit phone number.');
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                post_name: formData.phone,
                json_body: {
                    ...formData,
                    scores
                }
            };

            await fetch('https://yanchacms.metadei.in/wp-json/career-demo/v1/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            setFormSubmitted(true);
        } catch (error) {
            console.error('Submission failed', error);
            setFormSubmitted(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!formSubmitted) {
        return (
            <FullResultGate
                formData={formData}
                isSubmitting={isSubmitting}
                onChange={handleChange}
                onSubmit={handleSubmit}
            />
        );
    }

    const participantName = formData.name?.trim() || 'Your';
    const participantHeading = participantName === 'Your' ? 'Your' : `${participantName}'s`;
    const printSurface = typeof document !== 'undefined' && document.body
        ? createPortal(
            <FullResultsPrintPage
                report={report}
                participantHeading={participantHeading}
                participantName={participantName}
            />,
            document.body
        )
        : null;

    return (
        <>
            <style>{printStyles}</style>

            <div className="full-results-live-page full-report-shell flex flex-col items-center justify-center min-h-[80vh] w-full px-2 pb-20 pt-5 animate-fadeIn sm:px-4">
                <div className="w-full max-w-7xl mx-auto">
                    <div className="full-results-print-toolbar mb-6 flex justify-stretch sm:mb-8 sm:justify-end">
                        <button
                            type="button"
                            onClick={handlePrint}
                            disabled={isPreparingPrint}
                            className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-orange-500 px-6 py-3 font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:from-purple-700 hover:to-orange-600 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                        >
                            <Download size={20} />
                            <span>{isPreparingPrint ? 'Opening print preview…' : 'Download Full Report (PDF)'}</span>
                        </button>
                    </div>

                    <div className="space-y-8">
                        <h1 className="large-gradient-heading text-center brand-font">
                            Yancha Crystal Report for {participantName}
                        </h1>

                        <section className="rounded-[1.5rem] border border-[#efe6dd] bg-white/70 p-4 shadow-[0_22px_70px_rgba(0,0,0,0.06)] backdrop-blur-xl full-report-card sm:rounded-[2rem] sm:p-6 md:p-10">
                            <div className="mb-8">
                                <p className="text-xs font-black uppercase tracking-[0.28em] text-[#92278F] sm:text-sm sm:tracking-[0.35em]">{report.partA.intro.analysisTitle}</p>
                                <h2 className="mt-6 text-2xl font-black leading-tight text-[#171717] brand-font sm:text-3xl md:text-5xl">{report.partA.intro.title}</h2>
                            </div>

                            <div className="grid gap-6 xl:grid-cols-[1.08fr_0.92fr] xl:items-stretch">
                                <PartAIntroPanel intro={report.partA.intro} />
                                <PartACareerPreferencesPanel intro={report.partA.intro} />
                            </div>

                            <div className="mt-8 rounded-[1.5rem] border border-[#eadff0] bg-white/95 p-4 shadow-sm full-report-card sm:rounded-[1.85rem] sm:p-6">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <div>
                                        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#92278F] sm:text-sm sm:tracking-[0.22em]">Generated from top three traits</p>
                                        <h3 className="mt-2 text-2xl font-black text-[#171717] brand-font sm:text-3xl md:text-5xl">{report.interestCode}</h3>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        {report.partA.topTraits.map((trait) => (
                                            <span key={`top-code-${trait.code}`} className="rounded-full border px-3 py-1.5 text-xs font-black sm:px-4 sm:py-2 sm:text-sm" style={{ borderColor: `${trait.color}35`, color: trait.color, backgroundColor: `${trait.color}12` }}>
                                                {trait.title}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 space-y-8">
                                {report.partA.topTraits.map((trait, index) => (
                                    <div key={`part-a-${trait.code}`} className="grid min-w-0 gap-6 xl:grid-cols-[1fr_0.86fr] xl:items-start">
                                        <PartATraitExplanationCard trait={trait} index={index} />
                                        <PartATraitCareerPanel trait={trait} />
                                    </div>
                                ))}
                            </div>
                        </section>

                        <PartBReferenceSection report={report} />

                        <p className="rounded-[1.75rem] border border-[#eadff0] bg-white/90 px-5 py-4 text-center text-sm md:text-base italic leading-relaxed text-[#57534e] brand-text shadow-sm full-report-card">
                            These insights describe tendencies, not limits. Skills and behaviors can evolve with experience and support.
                        </p>
                    </div>
                </div>
            </div>

            {printSurface}
        </>
    );
};

export default FullResultsPage;
